import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { BookOpen, ChevronLeft, ChevronRight, ClipboardCheck, CloudUpload, Sparkles, Disc3, FolderTree, Heart, History, Home, Library, ListMusic, LogOut, Mic2, Music2, Music4, PencilLine, Plus, Search, Settings, Tags } from 'lucide-react';
import { useAccess } from '../lib/access';
import { API_BASE } from '../api/client';
import { useMe, usePlaylistMutations, usePlaylists } from '../api/hooks';
import { toast } from '../ui/toast';
import { Brand } from './Wordmark';
import { SearchBox } from './SearchBox';

const NAV = [
  { to: '/', label: 'Accueil', icon: Home, end: true },
  { to: '/search', label: 'Rechercher', icon: Search },
];
type NavItem = { to: string; label: string; icon: typeof Home; when?: 'music' | 'stories' | 'upload' | 'admin' };
export const LIBRARY: NavItem[] = [
  { to: '/stories', label: 'Histoires', icon: BookOpen, when: 'stories' },
  { to: '/composers', label: 'Compositeurs', icon: Music4, when: 'music' },
  { to: '/albums', label: 'Albums', icon: Disc3, when: 'music' },
  { to: '/tracks', label: 'Titres', icon: Music2, when: 'music' },
  { to: '/artists', label: 'Artistes', icon: Mic2, when: 'music' },
  { to: '/genres', label: 'Genres', icon: Tags, when: 'music' },
  { to: '/folders', label: 'Dossiers', icon: FolderTree },
  { to: '/favorites', label: 'Favoris', icon: Heart },
  { to: '/history', label: 'Historique', icon: History },
  { to: '/uploads', label: 'Mes envois', icon: CloudUpload, when: 'upload' },
  { to: '/edit', label: 'Édition', icon: PencilLine, when: 'upload' },
  { to: '/report', label: 'Rapport', icon: ClipboardCheck, when: 'admin' },
];

/** Entrées de navigation autorisées pour la personne connectée */
export function useNavItems() {
  const a = useAccess();
  return LIBRARY.filter((n) => !n.when || (n.when === 'music' ? a.hasMusic : n.when === 'stories' ? a.hasStories : n.when === 'admin' ? a.isAdmin : a.canUpload));
}

export function Sidebar() {
  const lists = usePlaylists();
  const { create } = usePlaylistMutations();
  const nav = useNavigate();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const items = useNavItems();
  return (
    <aside className="sidebar">
      <Link to="/" className="sidebar__brand" aria-label="Accueil PUJOLiSIQUE">
        <Brand />
      </Link>
      <nav className="sidebar__nav" aria-label="Navigation principale">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className="navlink">
            <n.icon size={19} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <nav className="sidebar__nav" aria-label="Bibliothèque">
        <h2 className="sidebar__h">Bibliothèque</h2>
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} className="navlink">
            <n.icon size={19} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar__playlists">
        <div className="sidebar__plhead">
          <h2 className="sidebar__h">
            <NavLink to="/playlists">Playlists</NavLink>
          </h2>
          <button className="icon-btn" onClick={() => setNaming((v) => !v)} aria-label="Créer une playlist" title="Créer une playlist">
            <Plus size={17} />
          </button>
        </div>
        {naming && (
          <form
            className="sidebar__new"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              create.mutate(
                { name: name.trim(), trackIds: [] },
                {
                  onSuccess: (p) => {
                    setNaming(false);
                    setName('');
                    nav(`/playlist/${p.id}`);
                  },
                  onError: (err) => toast(err.message, 'error'),
                },
              );
            }}
          >
            <input className="input input--small" autoFocus placeholder="Nom de la playlist" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => !name && setNaming(false)} />
          </form>
        )}
        <ul>
          {lists.data?.map((p) => (
            <li key={p.id}>
              <NavLink to={`/playlist/${p.id}`} className="navlink navlink--pl">
                {p.smart ? <Sparkles size={16} aria-label="Playlist intelligente" /> : <ListMusic size={16} />}
                <span>{p.name}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export function TopBar() {
  const nav = useNavigate();
  const me = useMe();
  const [menu, setMenu] = useState(false);

  return (
    <div className="topbar">
      <div className="topbar__hist">
        <button className="icon-btn icon-btn--ring" onClick={() => nav(-1)} aria-label="Page précédente">
          <ChevronLeft size={18} />
        </button>
        <button className="icon-btn icon-btn--ring" onClick={() => nav(1)} aria-label="Page suivante">
          <ChevronRight size={18} />
        </button>
      </div>
      <Link to="/" className="topbar__brand" aria-label="Accueil">
        <Brand compact />
      </Link>
      <SearchBox />
      <div className="user">
        <button className="user__btn" onClick={() => setMenu((v) => !v)} aria-expanded={menu} aria-haspopup="menu" aria-label="Compte">
          <span className="user__avatar">{(me.data?.name ?? '?').slice(0, 1).toUpperCase()}</span>
          <span className="user__name">{me.data?.name}</span>
        </button>
        {menu && (
          <div className="menu user__menu" role="menu" onMouseLeave={() => setMenu(false)}>
            <div className="menu__title">
              {me.data?.name}
              <span className="muted"> ({me.data?.username})</span>
            </div>
            <Link className="menu__item" to="/settings" onClick={() => setMenu(false)} role="menuitem">
              <Settings size={16} />
              <span>Réglages</span>
            </Link>
            {me.data?.authMode === 'oidc' && (
              <a className="menu__item" href={`${API_BASE}/auth/logout`} role="menuitem">
                <LogOut size={16} />
                <span>Se déconnecter</span>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function BottomNav() {
  const { hasStories } = useAccess();
  return (
    <nav className="bottomnav" aria-label="Navigation">
      <NavLink to="/" end className="bottomnav__link">
        <Home size={22} />
        <span>Accueil</span>
      </NavLink>
      <NavLink to="/search" className="bottomnav__link">
        <Search size={22} />
        <span>Rechercher</span>
      </NavLink>
      {hasStories && (
        <NavLink to="/stories" className="bottomnav__link">
          <BookOpen size={22} />
          <span>Histoires</span>
        </NavLink>
      )}
      <NavLink to="/library" className="bottomnav__link">
        <Library size={22} />
        <span>Bibliothèque</span>
      </NavLink>
    </nav>
  );
}
